import { useMemo, useCallback } from "react";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { editSummary } from "app/containers/Chatbot/slice";
import { ChatbotConversation } from "app/containers/Chatbot/types";
import { selectHasCoachBoConversationHistoryEnabled } from "app/containers/AdminConsole/slice";
import { selectCurrentUserInfo } from "app/containers/Global/slice";
import { useCoachBoConversations } from "../../../common";

export interface IConversationItem {
  id: string;
  timestamp: number;
  displayTitle: string;
  title: string;
  summary: string;
  body: string;
  // Conversations without a summary row yet can't be edited (the API 401s).
  canEdit: boolean;
  conversation: ChatbotConversation;
}

export interface IFilters {
  search: string;
  // "yyyy-MM-dd", as emitted by TeamDateRangeFilter.
  startDate?: string;
  endDate?: string;
}

// Backend timestamps come back as either ISO or SQL ("yyyy-MM-dd HH:mm:ss").
const parseDate = (value?: string | null) =>
  DateTime.fromISO((value ?? "").replace(" ", "T"));

export const formatConversationDate = (value?: string | null) => {
  const date = parseDate(value);
  return date.isValid ? date.toFormat("LLLL d, yyyy") : "Unknown date";
};

const toItem = (conversation: ChatbotConversation): IConversationItem => {
  const date = formatConversationDate(conversation.created_at);
  const title = conversation.summary?.title ?? "";
  const fallbackTitle = conversation.weeklyCheckInLink
    ? "Check-in chat"
    : "Untitled chat";
  return {
    id: conversation.threadId,
    // Missing or unparseable dates sort last.
    timestamp: parseDate(conversation.created_at).toMillis() || 0,
    displayTitle: `${date} – ${title || fallbackTitle}`,
    title,
    summary: conversation.summary?.summary ?? "",
    body: conversation.summary?.summary || "No summary provided.",
    canEdit: !!conversation.summary,
    conversation,
  };
};

const matchesFilters = (item: IConversationItem, filters: IFilters) => {
  const search = filters.search.trim().toLowerCase();
  if (
    search &&
    !item.displayTitle.toLowerCase().includes(search) &&
    !item.body.toLowerCase().includes(search)
  ) {
    return false;
  }

  // Undated conversations can't match a date range.
  if ((filters.startDate || filters.endDate) && !item.timestamp) {
    return false;
  }

  if (
    filters.startDate &&
    item.timestamp <
      DateTime.fromISO(filters.startDate).startOf("day").toMillis()
  ) {
    return false;
  }

  if (
    filters.endDate &&
    item.timestamp > DateTime.fromISO(filters.endDate).endOf("day").toMillis()
  ) {
    return false;
  }

  return true;
};

// Same source as the side drawer and Chat History, including weekly check-ins.
export function useFilteredConversationsQuery(filters: IFilters) {
  const { search, startDate, endDate } = filters;
  const { conversations, status } = useCoachBoConversations();
  const isCompanyHistoryEnabled = useAppSelector(
    selectHasCoachBoConversationHistoryEnabled
  );
  const userInfo = useAppSelector(selectCurrentUserInfo);

  const items = useMemo(
    () => conversations.map(toItem).sort((a, b) => b.timestamp - a.timestamp),
    [conversations]
  );

  let historyStatus: "enabled" | "companyDisabled" | "userDisabled" = "enabled";
  if (!isCompanyHistoryEnabled) {
    historyStatus = "companyDisabled";
  } else if (userInfo && !userInfo.hasCoachBoConversationHistoryEnabled) {
    historyStatus = "userDisabled";
  }

  const data = useMemo(
    () =>
      items.filter((item) =>
        matchesFilters(item, { search, startDate, endDate })
      ),
    [items, search, startDate, endDate]
  );

  return {
    data,
    totalCount: items.length,
    isLoading: status === "idle" || status === "loading" || !userInfo,
    historyStatus,
  };
}

export function useEditConversation() {
  const dispatch = useAppDispatch();

  return useCallback(
    async (
      conversation: ChatbotConversation,
      title: string,
      summary: string
    ) => {
      try {
        await dispatch(
          editSummary({
            threadId: conversation.threadId,
            title,
            summary,
            // The API requires a string; keep whatever commitment exists.
            commitment: conversation.summary?.commitment ?? "",
          })
        ).unwrap();
      } catch (error) {
        toast.error("Couldn't save your changes. Please try again.");
        throw error;
      }
    },
    [dispatch]
  );
}
